// Generated by ts-to-zod
import { z } from 'zod'

import { BLOCK_TYPE } from '../../../types/next/enums'

import {
  PRODUCTS_FUNCTIONS,
  CATALOG,
  PRODUCTS_TYPE,
  RECURRENCE_UNIT,
  TILE_ALIGNMENT,
  CTA_OPTION,
  SELECTION_OPTION
} from './../../../types/next/blocks/productsBlock'
import { iBlockSchema, blockBaseSettingsSchema } from './baseBlock.zod'
import { logicOperatorSchema } from './enums.zod'
import { referenceIdSchema } from './ids.zod'
import { functionClauseSchema } from './logic.zod'
import { blockReferenceSchema } from './references.zod'

export const productsFunctionsSchema = z.nativeEnum(PRODUCTS_FUNCTIONS)

export const catalogSchema = z.nativeEnum(CATALOG)

export const productsTypeSchema = z.nativeEnum(PRODUCTS_TYPE)

export const recurrenceUnitSchema = z.nativeEnum(RECURRENCE_UNIT)

export const tileAlignmentSchema = z.nativeEnum(TILE_ALIGNMENT)

export const ctaOptionSchema = z.nativeEnum(CTA_OPTION)

export const selectionOptionSchema = z.nativeEnum(SELECTION_OPTION)

export const tileLayoutPropertySchema = z.object({
  order: z.number(),
  isConfigurable: z.boolean(),
  isVisible: z.boolean(),
  key: z.string()
})

export const iProductAndPriceSchema = z.object({
  productId: z.string(),
  priceId: z.string()
})

export const tileLayoutSchema = z.object({
  variant: z.string(),
  properties: z.object({
    headline: tileLayoutPropertySchema,
    image: tileLayoutPropertySchema,
    description: tileLayoutPropertySchema,
    price: tileLayoutPropertySchema.and(
      z.object({
        showTrailingDecimalZeros: z.boolean(),
        showRecurringTotal: z.boolean(),
        recurrenceUnit: recurrenceUnitSchema.optional(),
        showUnitaryAverage: z.boolean(),
        featurePriceAfterCashback: z.boolean()
      })
    ),
    priceComponents: tileLayoutPropertySchema.and(
      z.object({
        showTrailingDecimalZeros: z.boolean()
      })
    ),
    cta: tileLayoutPropertySchema.and(
      z.object({
        option: ctaOptionSchema
      })
    ),
    features: tileLayoutPropertySchema.and(
      z.object({
        maxCount: z.number()
      })
    ),
    details: tileLayoutPropertySchema.and(
      z.object({
        showMarketDataCharts: z.boolean(),
        showGetAgPriceDetails: z.boolean(),
        expandGetAgPriceDetailsByDefault: z.boolean().optional()
      })
    ),
    files: tileLayoutPropertySchema
  })
})

export const productFilterSchema = iProductAndPriceSchema.extend({
  excludeOnValue: z
    .union([z.string(), z.boolean()])
    .describe(
      'When the comparison returns:\n- true: the product is excluded/filtered out from the block\n- false: the product is included in the block'
    )
})

export const productReferenceSchema = iProductAndPriceSchema

export const productPropertiesSchema = productReferenceSchema.extend({
  isFeatured: z.boolean(),
  featureAppearance: z
    .object({
      labelText: z.string().optional(),
      borderColor: z
        .string()
        .optional()
        .describe('The color of the border in hex format'),
      labelColor: z
        .string()
        .optional()
        .describe('The text color of the label in hex format')
    })
    .optional()
})

export const productsBlockSettingsSchema = z.object({
  layout: tileLayoutSchema,
  alignment: tileAlignmentSchema,
  selection: selectionOptionSchema
})

export const externalCatalogSettingsSchema = productsBlockSettingsSchema.extend(
  {
    catalog: z.literal(CATALOG.EXTERNAL),
    integrationId: z.string()
  }
)

export const epilotCatalogSettingsSchema = productsBlockSettingsSchema.extend({
  catalog: z.literal(CATALOG.EPILOT),
  productsType: productsTypeSchema,
  products: z.array(productPropertiesSchema)
})

const iCatalogBasedProductsBlockSchema = iBlockSchema.extend({
  type: z.literal(BLOCK_TYPE.PRODUCTS),
  settings: blockBaseSettingsSchema.and(z.object({}))
})

export const crossSellingProductsReferenceSchema = blockReferenceSchema.extend({
  meaning: z.literal('cross-selling-product'),
  products: z.array(productReferenceSchema),
  enabled: z.boolean()
})

export const simplePriceSchema = z.object({
  priceId: z.string()
})

export const compositePriceComponentSchema = z.object({
  priceId: z.string(),
  componentId: z.string()
})

export const directMappingMetadataSchema = z.object({
  productId: z.string(),
  price: z.union([simplePriceSchema, compositePriceComponentSchema])
})

export const indirectMappingMetadataSchema = z.object({
  unit: z.union([z.string(), z.undefined()]),
  recurrenceType: z.union([
    z.literal('one-time'),
    z.literal('recurring'),
    z.literal('any')
  ])
})

export const variablePriceReferenceSchema = blockReferenceSchema.extend({
  meaning: z.literal('variable-price'),
  metadata: z.union([
    directMappingMetadataSchema,
    indirectMappingMetadataSchema
  ])
})

export const optionalPriceComponentReferenceSchema =
  blockReferenceSchema.extend({
    meaning: z.literal('optional-price'),
    operator: z.literal('EQUALS'),
    value: z.boolean(),
    metadata: z.object({
      productId: z.string(),
      price: compositePriceComponentSchema
    })
  })

export const gridFeeAreaReferenceSchema = blockReferenceSchema.extend({
  meaning: z.literal('grid-fee-area')
})

export const productFilterReferenceSchema = blockReferenceSchema.extend({
  meaning: z.literal('product-filter'),
  filters: z.array(productFilterSchema)
})

export const productsAvailabilityCheckReferenceSchema =
  blockReferenceSchema.extend({
    meaning: z.literal('availability check for products')
  })

export const productsBlockOwnReferencesSchema = z.union([
  optionalPriceComponentReferenceSchema,
  variablePriceReferenceSchema,
  crossSellingProductsReferenceSchema,
  gridFeeAreaReferenceSchema,
  productFilterReferenceSchema,
  productsAvailabilityCheckReferenceSchema
])

export const epilotCatalogProductsBlockSchema =
  iCatalogBasedProductsBlockSchema.extend({
    settings: blockBaseSettingsSchema.and(epilotCatalogSettingsSchema).and(
      z.object({
        catalog: z.literal(CATALOG.EPILOT)
      })
    ),
    references: z.record(referenceIdSchema, productsBlockOwnReferencesSchema)
  })

export const externalCatalogProductsBlockSchema =
  iCatalogBasedProductsBlockSchema.extend({
    settings: blockBaseSettingsSchema.and(externalCatalogSettingsSchema).and(
      z.object({
        catalog: z.literal(CATALOG.EXTERNAL)
      })
    ),
    references: z.never().optional()
  })

export const productsBlockSchema = z.union([
  epilotCatalogProductsBlockSchema,
  externalCatalogProductsBlockSchema
])

export const productSelectionFunctionClauseSchema = functionClauseSchema.extend(
  {
    functionName: z.literal(PRODUCTS_FUNCTIONS.PRODUCT_SELECTION),
    args: z.object({
      operator: logicOperatorSchema
    })
  }
)

export const productSelectionComparisonValueSchema = z.array(
  iProductAndPriceSchema
)

export const productSelectionTupleSchema = z.tuple([z.string(), z.string()])

export const productSelectionTuplesSchema = z.array(productSelectionTupleSchema)

const selectedProductTileSchema = z.any()

export const productsBlockValueSchema = z.union([
  selectedProductTileSchema,
  z.array(selectedProductTileSchema)
])
